import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CssThemes, EmailType } from 'src/app/shared/common';
import { ApplicationType, JWTTokenValidation } from '../../common';
import { JwtDecoderService } from '../../helper';
import { LoginForm } from '../../interface';
import { AuthenticationApi } from '../api/authentication-api';
import { AuthUser } from '../model/auth-user-model';
import { Repository } from './repository';
import { ThemesService } from 'src/app/core/services/themes.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationRepository extends Repository {
  public currentUser$: BehaviorSubject<AuthUser> = new BehaviorSubject<AuthUser>(new AuthUser());

  constructor(
    private authenticationApi: AuthenticationApi, 
    private jwtDecoderService: JwtDecoderService, 
    private themesService: ThemesService
  ) {
    super();
    this.initializeUser();
  }
  // login
  login(loginData: LoginForm): Observable<string> {
    return this.authenticationApi.login(loginData).pipe(
      tap((authToken: string) => {
        this.jwtDecoderService.saveToken(authToken);
        this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWT());
        this.themesService.currentTheme = this.getApplicationTheme();
      })
    );
  }
  // checking if user is logged in
  isLoggedIn(): boolean {
    return this.jwtDecoderService.isThereValidToken() !== JWTTokenValidation.NOT_FOUND;
  }
  //forget password
  forgetPassword(email: EmailType): Observable<string> {
    return this.authenticationApi.forgetPassword(email);
  }

  //logout
  logout(): Observable<boolean> {
    return this.authenticationApi.logout().pipe(
      tap((res) => {
        this.jwtDecoderService.removeCurrentToken();
        this.currentUser$.next(null);
        this.themesService.setTheme(this.themesService.defaultTheme);
      })
    );
  }

  getApplicationTypeFromToken(): ApplicationType | null {
    const userApps = this.currentUser$.value.appsType || [];
    const userHasApplications = userApps.length > 0;
    const userHasModdakirApp = userApps.includes(ApplicationType.MODDAKIR);

    if (!userHasApplications) { 
      return null;
    }
    if (userHasModdakirApp) {
      return ApplicationType.MODDAKIR;
    }
    return userApps.find(Boolean);
  }

  // privates
  private initializeUser(): void {
    if (this.jwtDecoderService.isThereValidToken() === JWTTokenValidation.VALID) {
      this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWT());
    }
  }

  private getApplicationTheme(): CssThemes {
    const userApps = this.currentUser$.value.appsType || [];
    const appType = this.getApplicationTypeFromToken();
    const isSuperAdmin = !appType && !userApps.length;

    if(isSuperAdmin) {
      return CssThemes.SUPER_ADMIN;
    }
    if(appType) {
      return Object.values(CssThemes).find(cssTheme => cssTheme.toString() === appType.toString());
    }
    return this.themesService.defaultTheme
  }
}
